import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useEffect, useId, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { INTERESTS } from '@/constants/interests'
import { CONTACT, LINKS } from '@/constants/site'

type Status = 'idle' | 'sending' | 'sent' | 'error'

type Fields = {
    name: string
    phone: string
    company: string
    email: string
    interest: string
    message: string
    website: string
}

const EMPTY: Fields = {
    name: '',
    phone: '',
    company: '',
    email: '',
    interest: 'hele-svenn',
    message: '',
    website: ''
}

interface FieldProps {
    id: string
    label: string
    optional?: boolean
    error?: string | null
    children: ReactNode
    className?: string
}

const Field: FC<FieldProps> = ({ id, label, optional, error, children, className }): ReactNode => (
    <div className={cn('cb-field', className)} data-invalid={error ? 'true' : undefined}>
        <label htmlFor={id} className='t-label flex justify-between gap-4 text-slate'>
            {label}
            {optional && <span className='opacity-70'>Valgfritt</span>}
        </label>
        {children}
        {error && <p id={`${id}-error`} className='mt-2 text-sm font-medium text-ember' role='alert'>{error}</p>}
    </div>
)

/** The call-back request: saved to the database and forwarded to the Svenn team by email. */
export const CallbackForm: FC = (): ReactNode => {
    const router = useRouter()
    const uid = useId()
    const [fields, setFields] = useState<Fields>(EMPTY)
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState<{ field?: string, message: string } | null>(null)

    useEffect(() => {
        if (!router.isReady) return

        const tema = typeof router.query.tema === 'string' ? router.query.tema : null

        if (tema && INTERESTS.some(interest => interest.id === tema)) {
            setFields(current => ({ ...current, interest: tema }))
        }
    }, [router.isReady, router.query.tema])

    const set = (key: keyof Fields) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const { value } = event.target

        setFields(current => ({ ...current, [key]: value }))

        if (error?.field === key) setError(null)
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (fields.name.trim().length < 2) {
            setError({ field: 'name', message: 'Skriv inn navnet ditt.' })
            return
        }

        if (fields.phone.replace(/\D/g, '').length < 8) {
            setError({ field: 'phone', message: 'Skriv inn et gyldig telefonnummer.' })
            return
        }

        setStatus('sending')
        setError(null)

        try {
            const response = await fetch('/api/callback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...fields, sourcePage: router.asPath })
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, message?: string, field?: string } | null

            if (!response.ok || !result?.ok) {
                setStatus('error')
                setError({ field: result?.field, message: result?.message ?? 'Noe gikk galt. Prøv igjen om litt.' })
                return
            }

            setStatus('sent')
        }
        catch {
            setStatus('error')
            setError({ message: `Vi fikk ikke kontakt. Sjekk nettet og prøv igjen, eller ring oss på ${CONTACT.phone}.` })
        }
    }

    const fieldError = (key: keyof Fields): string | null => (error?.field === key ? error.message : null)
    const id = (key: string): string => `${uid}-${key}`

    if (status === 'sent') {
        const first = fields.name.trim().split(' ')[0]

        return (
            <div className='animate-in fade-in slide-in-from-bottom-4 duration-700' role='status'>
                <span className='flex size-14 items-center justify-center bg-lime text-night'>
                    <Check className='size-7' strokeWidth={2.5} aria-hidden='true' />
                </span>
                <p className='t-h1 mt-8 balance'>Takk, {first}.</p>
                <p className='t-lead mt-5 max-w-md text-slate pretty'>
                    Vi ringer deg på <span className='font-semibold text-night tabular'>{fields.phone}</span> i åpningstiden, hverdager 08 til 16.
                </p>
                <button
                    type='button'
                    onClick={() => {
                        setFields(EMPTY)
                        setStatus('idle')
                    }}
                    className='u-link-static mt-10 text-sm font-semibold'
                >
                    Send en ny forespørsel
                </button>
            </div>
        )
    }

    return (
        <form onSubmit={onSubmit} noValidate className='grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-2'>
            <Field id={id('name')} label='Navn' error={fieldError('name')}>
                <input
                    id={id('name')}
                    name='name'
                    autoComplete='name'
                    required
                    value={fields.name}
                    onChange={set('name')}
                    aria-invalid={Boolean(fieldError('name'))}
                    aria-describedby={fieldError('name') ? `${id('name')}-error` : undefined}
                    className='cb-input'
                />
            </Field>

            <Field id={id('phone')} label='Telefon' error={fieldError('phone')}>
                <input
                    id={id('phone')}
                    name='phone'
                    type='tel'
                    inputMode='tel'
                    autoComplete='tel'
                    required
                    value={fields.phone}
                    onChange={set('phone')}
                    aria-invalid={Boolean(fieldError('phone'))}
                    aria-describedby={fieldError('phone') ? `${id('phone')}-error` : undefined}
                    className='cb-input tabular'
                />
            </Field>

            <Field id={id('company')} label='Bedrift' optional>
                <input id={id('company')} name='company' autoComplete='organization' value={fields.company} onChange={set('company')} className='cb-input' />
            </Field>

            <Field id={id('email')} label='E-post' optional error={fieldError('email')}>
                <input
                    id={id('email')}
                    name='email'
                    type='email'
                    autoComplete='email'
                    value={fields.email}
                    onChange={set('email')}
                    aria-invalid={Boolean(fieldError('email'))}
                    aria-describedby={fieldError('email') ? `${id('email')}-error` : undefined}
                    className='cb-input'
                />
            </Field>

            <Field id={id('interest')} label='Hva vil du snakke om?' className='md:col-span-2'>
                <select id={id('interest')} name='interest' value={fields.interest} onChange={set('interest')} className='cb-input cb-select'>
                    {INTERESTS.map(interest => (
                        <option key={interest.id} value={interest.id}>{interest.label}</option>
                    ))}
                </select>
            </Field>

            <Field id={id('message')} label='Noe vi bør vite før vi ringer?' optional className='md:col-span-2'>
                <textarea id={id('message')} name='message' rows={3} value={fields.message} onChange={set('message')} className='cb-input resize-y' />
            </Field>

            <div className='hidden' aria-hidden='true'>
                <label htmlFor={id('website')}>Nettside</label>
                <input id={id('website')} name='website' tabIndex={-1} autoComplete='off' value={fields.website} onChange={set('website')} />
            </div>

            <div className='flex flex-col gap-5 md:col-span-2'>
                {error && !error.field && (
                    <p className='bg-butter px-4 py-3 text-sm font-medium text-night' role='alert'>{error.message}</p>
                )}

                <div className='flex flex-wrap items-center gap-x-8 gap-y-4'>
                    <button type='submit' disabled={status === 'sending'} className='btn btn-lg min-w-[13rem] disabled:opacity-70'>
                        <span>{status === 'sending' ? 'Sender' : 'Ring meg'}</span>
                        {status === 'sending'
                            ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                            : <ArrowRight className='btn-arrow size-4' aria-hidden='true' />}
                    </button>

                    <p className='max-w-xs text-xs leading-relaxed text-slate'>
                        Vi bruker opplysningene bare til å ringe deg. Les mer i{' '}
                        <a href={LINKS.privacy} target='_blank' rel='noopener noreferrer' className='u-link-static'>personvernerklæringen</a>.
                    </p>
                </div>
            </div>
        </form>
    )
}
